import React from "react";
import { useNavigate, useLocation } from "react-router-dom";

const ViewPayment = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const p = location.state?.payment;

    if (!p) {
        navigate(-1);
        return null;
    }

    const mode = (p.payment_mode || "").toLowerCase();
    const isCheque = mode.includes("cheque") || mode.includes("dd");
    const isUPI = mode.includes("phone") || mode.includes("google") || mode.includes("upi") || mode.includes("online");

    const fmt = (v) => {
        const n = Number(v);
        return isNaN(n) || n === 0 ? "—" : "₹" + n.toLocaleString("en-IN");
    };

    // ========== PDF Generation ==========
    const generatePDFContent = () => {
        return `
<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Payment Receipt - ${p.payment_id || p.id}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family:'Inter',sans-serif; color:#1e293b; background:#fff; padding:0; }
  .receipt { max-width:700px; margin:0 auto; padding:40px 36px; }

  /* Letterhead */
  .letterhead { text-align:center; border-bottom:3px solid #1e40af; padding-bottom:20px; margin-bottom:24px; }
  .company-name { font-size:26px; font-weight:800; color:#1e40af; text-transform:uppercase; letter-spacing:1.5px; }
  .company-sub { font-size:11px; color:#64748b; margin-top:4px; letter-spacing:.5px; font-weight:500; }
  .company-tag { font-size:10px; color:#94a3b8; margin-top:2px; }

  /* Receipt Header */
  .receipt-title-bar { display:flex; justify-content:space-between; align-items:center; margin-bottom:20px; padding:14px 18px; background:#f0f4ff; border-radius:10px; border:1px solid #dbeafe; }
  .receipt-title { font-size:18px; font-weight:700; color:#1e40af; }
  .receipt-id { font-size:13px; color:#475569; font-weight:600; }
  .receipt-date { font-size:12px; color:#64748b; margin-top:2px; }

  /* Status Badge */
  .status-wrap { text-align:center; margin-bottom:22px; }
  .status-badge { display:inline-block; padding:6px 22px; border-radius:20px; font-size:13px; font-weight:700; text-transform:uppercase; letter-spacing:.5px; }
  .status-confirmed { background:#dcfce7; color:#15803d; border:1.5px solid #86efac; }
  .status-received { background:#fef9c3; color:#a16207; border:1.5px solid #fde047; }
  .status-other { background:#f1f5f9; color:#475569; border:1.5px solid #cbd5e1; }

  /* Detail Grid */
  .section { margin-bottom:22px; }
  .section-title { font-size:13px; font-weight:700; color:#1e40af; text-transform:uppercase; letter-spacing:.6px; margin-bottom:12px; padding-bottom:8px; border-bottom:1px solid #e2e8f0; }
  .detail-grid { display:grid; grid-template-columns:1fr 1fr; gap:0; }
  .detail-row { display:flex; justify-content:space-between; padding:10px 14px; border-bottom:1px dashed #f1f5f9; }
  .detail-row:nth-child(odd) { background:#fafbff; }
  .detail-label { font-size:13px; color:#64748b; font-weight:500; }
  .detail-value { font-size:13px; color:#1e293b; font-weight:600; text-align:right; }

  /* Amount Highlight */
  .amount-box { background:linear-gradient(135deg,#1e40af,#3b82f6); color:#fff; padding:20px 24px; border-radius:14px; text-align:center; margin:20px 0; }
  .amount-label { font-size:12px; opacity:.85; font-weight:500; text-transform:uppercase; letter-spacing:.5px; }
  .amount-value { font-size:32px; font-weight:800; margin-top:4px; letter-spacing:-.5px; }

  /* Footer */
  .footer { border-top:2px solid #e2e8f0; padding-top:18px; margin-top:28px; display:flex; justify-content:space-between; align-items:flex-end; }
  .footer-left { font-size:11px; color:#94a3b8; line-height:1.7; }
  .footer-right { text-align:right; }
  .stamp-area { font-size:11px; color:#94a3b8; border-top:1px solid #cbd5e1; padding-top:8px; min-width:160px; text-align:center; }

  .watermark { position:fixed; top:50%; left:50%; transform:translate(-50%,-50%) rotate(-30deg); font-size:80px; color:rgba(30,64,175,.04); font-weight:800; text-transform:uppercase; pointer-events:none; z-index:-1; }

  @media print {
    body { padding:0; }
    .receipt { padding:30px 24px; }
    @page { margin:15mm; size:A4; }
  }
</style>
</head>
<body>
<div class="watermark">PAYMENT RECEIPT</div>
<div class="receipt">
  <!-- Letterhead -->
  <div class="letterhead">
    <div class="company-name">Sai Nath Buildcon Pvt. Ltd.</div>
    <div class="company-sub">Real Estate Development & Construction</div>
    <div class="company-tag">GSTIN: XXXXXXXXXXXX | CIN: XXXXXXXXXXXXX</div>
  </div>

  <!-- Receipt Title -->
  <div class="receipt-title-bar">
    <div>
      <div class="receipt-title">Payment Receipt</div>
      <div class="receipt-date">Date: ${p.payment_collect || p.date || new Date().toLocaleDateString("en-IN")}</div>
    </div>
    <div style="text-align:right">
      <div class="receipt-id">Receipt #${p.payment_id || p.id || "—"}</div>
      <div class="receipt-date">Booking: ${p.booking_id || "—"}</div>
    </div>
  </div>

  <!-- Status -->
  <div class="status-wrap">
    <span class="status-badge ${(p.status || "").toLowerCase() === "confirmed" ? "status-confirmed" : (p.status || "").toLowerCase() === "received" ? "status-received" : "status-other"}">${p.status || "—"}</span>
  </div>

  <!-- Customer Details -->
  <div class="section">
    <div class="section-title">Customer Details</div>
    <div class="detail-row"><span class="detail-label">Customer Name</span><span class="detail-value">${p.customer_name || p.name || "—"}</span></div>
    <div class="detail-row"><span class="detail-label">Phone</span><span class="detail-value">${p.cust_phone || p.phone || "—"}</span></div>
    <div class="detail-row"><span class="detail-label">Unit</span><span class="detail-value">${p.unit || "—"}</span></div>
    <div class="detail-row"><span class="detail-label">Project Name</span><span class="detail-value">${p.project_name || "—"}</span></div>
  </div>

  <!-- Payment Details -->
  <div class="section">
    <div class="section-title">Payment Details</div>
    <div class="detail-row"><span class="detail-label">Payment ID</span><span class="detail-value">${p.payment_id || "—"}</span></div>
    <div class="detail-row"><span class="detail-label">Booking ID</span><span class="detail-value">${p.booking_id || "—"}</span></div>
    <div class="detail-row"><span class="detail-label">Payment Mode</span><span class="detail-value">${p.payment_mode || "—"}</span></div>
    <div class="detail-row"><span class="detail-label">Payment Part</span><span class="detail-value">${p.payment_part || "—"}</span></div>
    <div class="detail-row"><span class="detail-label">Payment For</span><span class="detail-value">${p.payment_for || "—"}</span></div>
    <div class="detail-row"><span class="detail-label">Collection Date</span><span class="detail-value">${p.payment_collect || "—"}</span></div>
    ${isUPI && (p.utr || p.transaction_id) ? `<div class="detail-row"><span class="detail-label">UTR / Ref No.</span><span class="detail-value">${p.utr || p.transaction_id}</span></div>` : ""}
    ${isCheque && (p.cheque_bank || p.bank_name) ? `<div class="detail-row"><span class="detail-label">Bank Name</span><span class="detail-value">${p.cheque_bank || p.bank_name}</span></div>` : ""}
    ${isCheque && p.cheque_no ? `<div class="detail-row"><span class="detail-label">Cheque/DD No.</span><span class="detail-value">${p.cheque_no}</span></div>` : ""}
    ${isCheque && p.cheque ? `<div class="detail-row"><span class="detail-label">Cheque Date</span><span class="detail-value">${p.cheque}</span></div>` : ""}
    ${(!isUPI && !isCheque && p.transaction_id) ? `<div class="detail-row"><span class="detail-label">Transaction ID</span><span class="detail-value">${p.transaction_id}</span></div>` : ""}
    ${p.comment ? `<div class="detail-row"><span class="detail-label">Remarks</span><span class="detail-value">${p.comment}</span></div>` : ""}
  </div>

  <!-- Amount -->
  <div class="amount-box">
    <div class="amount-label">Amount Received</div>
    <div class="amount-value">${fmt(p.amount)}</div>
  </div>

  <!-- Footer -->
  <div class="footer">
    <div class="footer-left">
      This is a computer-generated receipt.<br/>
      For any queries, contact Sai Nath Buildcon Pvt. Ltd.<br/>
      Generated on: ${new Date().toLocaleString("en-IN")}
    </div>
    <div class="footer-right">
      <div class="stamp-area">Authorized Signatory</div>
    </div>
  </div>
</div>
</body>
</html>`;
    };

    const handleDownloadPDF = () => {
        const content = generatePDFContent();
        const printWindow = window.open("", "_blank");
        printWindow.document.write(content);
        printWindow.document.close();
        // Wait for fonts to load then trigger print
        printWindow.onload = () => {
            setTimeout(() => {
                printWindow.print();
            }, 500);
        };
    };

    const handleShareWhatsApp = () => {
        const text = `*PAYMENT RECEIPT*
━━━━━━━━━━━━━
*Sai Nath Buildcon Pvt. Ltd.*
━━━━━━━━━━━━━

*Receipt #:* ${p.payment_id || p.id || "—"}
*Booking ID:* ${p.booking_id || "—"}
*Date:* ${p.payment_collect || p.date || "—"}

*CUSTOMER DETAILS*
Name: ${p.customer_name || p.name || "—"}
Phone: ${p.cust_phone || p.phone || "—"}
Unit: ${p.unit || "—"}
Project: ${p.project_name || "—"}

*PAYMENT DETAILS*
Amount: ${fmt(p.amount)}
Mode: ${p.payment_mode || "—"}
Part: ${p.payment_part || "—"}
For: ${p.payment_for || "—"}
Status: ${p.status || "—"}
${isUPI && (p.utr || p.transaction_id) ? `UTR / Ref: ${p.utr || p.transaction_id}` : ""}
${isCheque ? `Bank: ${p.cheque_bank || p.bank_name || "—"}\nCheque No: ${p.cheque_no || "—"}\nCheque Date: ${p.cheque || "—"}` : ""}
${!isUPI && !isCheque && p.transaction_id ? `Transaction ID: ${p.transaction_id}` : ""}
${p.comment ? `Remarks: ${p.comment}` : ""}

━━━━━━━━━━━━━
_This is a system generated receipt from Sai Nath Buildcon Pvt. Ltd._`;

        const phone = p.cust_phone || p.phone || "";
        const encodedText = encodeURIComponent(text.replace(/\n{3,}/g, "\n\n").trim());
        const url = phone
            ? `https://wa.me/${phone.replace(/\D/g, "").replace(/^(\d{10})$/, "91$1")}?text=${encodedText}`
            : `https://wa.me/?text=${encodedText}`;
        window.open(url, "_blank");
    };

    const getStatusClass = (s) => {
        if (!s) return "vp-badge-na";
        const sl = s.toLowerCase();
        if (sl === "confirmed") return "vp-badge-confirmed";
        if (sl === "received") return "vp-badge-received";
        if (sl === "pending") return "vp-badge-pending";
        return "vp-badge-na";
    };

    // Gather all fields to display
    const sections = [
        {
            title: "👤 Customer Information",
            fields: [
                { label: "Customer Name", value: p.customer_name || p.name },
                { label: "Phone", value: p.cust_phone || p.phone },
                { label: "Unit", value: p.unit },
                { label: "Project", value: p.project_name },
            ],
        },
        {
            title: "💳 Payment Information",
            fields: [
                { label: "Payment ID", value: p.payment_id },
                { label: "Booking ID", value: p.booking_id },
                { label: "Amount", value: fmt(p.amount), highlight: true },
                { label: "Payment Mode", value: p.payment_mode },
                { label: "Payment Part", value: p.payment_part },
                { label: "Payment For", value: p.payment_for },
                { label: "Received By ", value: p.received_by },
                { label: "Handover To", value: p.handover },
                { label: "Status", value: p.status, badge: true },
            ],
        }
    ];

    if (isCheque) {
        sections.push({
            title: "🏦 Cheque / DD Details",
            fields: [
                { label: "Bank Name", value: p.cheque_bank || p.bank_name },
                { label: "Cheque No.", value: p.cheque_no },
                { label: "Cheque Date", value: p.cheque },
            ].filter(f => f.value),
        });
    } else if (isUPI) {
        sections.push({
            title: "📱 Digital Payment Info",
            fields: [
                { label: "UTR / Transaction ID", value: p.utr || p.transaction_id },
            ].filter(f => f.value),
        });
    }

    sections.push({
        title: "📅 Date & Reference",
        fields: [
            { label: "Collection Date", value: p.payment_collect },
            { label: "Created Date", value: p.date || p.created_at },
            (!isUPI && !isCheque) ? { label: "Transaction ID", value: p.transaction_id } : null,
            { label: "Remarks", value: p.comment || p.description },
        ].filter(f => f && f.value),
    });

    return (
        <>
            <style>{`
        .vp-page { max-width:900px; margin:0 auto; }

        .vp-hdr { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:24px; }
        .vp-hdr-left { display:flex; align-items:center; gap:14px; }
        .vp-back { padding:8px 16px; background:#f8fafc; border:1px solid #e2e8f0; border-radius:10px; color:#334155; cursor:pointer; font-size:14px; font-weight:500; font-family:'Inter',sans-serif; transition:all .2s; display:flex; align-items:center; gap:6px; }
        .vp-back:hover { background:#f1f5f9; border-color:#cbd5e1; }
        .vp-title { font-size:22px; font-weight:700; color:#0f172a; }
        .vp-sub { font-size:13px; color:#64748b; }

        .vp-actions { display:flex; gap:10px; flex-wrap:wrap; }
        .vp-btn { padding:10px 20px; border-radius:10px; font-size:13px; font-weight:600; cursor:pointer; font-family:'Inter',sans-serif; transition:all .2s; display:flex; align-items:center; gap:8px; border:none; }
        .vp-btn:hover { transform:translateY(-1px); }

        .vp-btn-pdf { background:linear-gradient(135deg,#dc2626,#ef4444); color:#fff; box-shadow:0 2px 10px rgba(220,38,38,.2); }
        .vp-btn-pdf:hover { box-shadow:0 4px 16px rgba(220,38,38,.3); }
        .vp-btn-wa { background:linear-gradient(135deg,#16a34a,#22c55e); color:#fff; box-shadow:0 2px 10px rgba(22,163,74,.2); }
        .vp-btn-wa:hover { box-shadow:0 4px 16px rgba(22,163,74,.3); }
        .vp-btn-edit { background:#fff; color:#2563eb; border:1px solid #bfdbfe; }
        .vp-btn-edit:hover { background:#eff6ff; box-shadow:0 2px 10px rgba(37,99,235,.12); }

        /* Amount Card */
        .vp-amt-card { background:linear-gradient(135deg,#1e40af,#3b82f6); border-radius:16px; padding:24px 28px; color:#fff; display:flex; justify-content:space-between; align-items:center; margin-bottom:20px; position:relative; overflow:hidden; }
        .vp-amt-card::before { content:''; position:absolute; top:-40px; right:-40px; width:120px; height:120px; border-radius:50%; background:rgba(255,255,255,.08); }
        .vp-amt-card::after { content:''; position:absolute; bottom:-20px; left:-20px; width:80px; height:80px; border-radius:50%; background:rgba(255,255,255,.05); }
        .vp-amt-label { font-size:13px; opacity:.8; font-weight:500; text-transform:uppercase; letter-spacing:.5px; }
        .vp-amt-value { font-size:32px; font-weight:800; margin-top:4px; letter-spacing:-.5px; }
        .vp-amt-right { text-align:right; z-index:1; }
        .vp-amt-status { display:inline-block; padding:6px 16px; border-radius:20px; font-size:12px; font-weight:700; text-transform:uppercase; letter-spacing:.4px; }
        .vp-amt-confirmed { background:rgba(255,255,255,.2); color:#fff; }
        .vp-amt-pending { background:rgba(251,191,36,.3); color:#fef3c7; }

        /* Sections */
        .vp-card { background:#fff; border:1px solid #e2e8f0; border-radius:16px; padding:24px; box-shadow:0 1px 3px rgba(0,0,0,.04); margin-bottom:16px; }
        .vp-sec-title { font-size:15px; font-weight:700; color:#0f172a; margin-bottom:16px; padding-bottom:10px; border-bottom:1px solid #f1f5f9; display:flex; align-items:center; gap:8px; }
        .vp-grid { display:grid; grid-template-columns:1fr 1fr; gap:0; }
        .vp-row { display:flex; justify-content:space-between; align-items:center; padding:11px 14px; border-bottom:1px dashed #f1f5f9; transition:background .15s; }
        .vp-row:hover { background:#fafbff; }
        .vp-row:last-child { border-bottom:none; }
        .vp-label { font-size:13px; color:#64748b; font-weight:500; }
        .vp-value { font-size:13px; color:#1e293b; font-weight:600; text-align:right; }
        .vp-value-hl { font-size:15px; font-weight:800; color:#1e40af; }

        .vp-badge { display:inline-block; padding:4px 12px; border-radius:20px; font-size:11px; font-weight:600; }
        .vp-badge-confirmed { background:#ecfdf5; color:#059669; border:1px solid #a7f3d0; }
        .vp-badge-received { background:#fef9c3; color:#a16207; border:1px solid #fde047; }
        .vp-badge-pending { background:#fffbeb; color:#d97706; border:1px solid #fcd34d; }
        .vp-badge-na { background:#f8fafc; color:#94a3b8; border:1px solid #e2e8f0; }

        @media(max-width:640px){
          .vp-hdr{flex-direction:column;align-items:stretch;}
          .vp-actions{justify-content:stretch;}
          .vp-actions .vp-btn{flex:1;justify-content:center;}
          .vp-grid{grid-template-columns:1fr;}
          .vp-amt-card{flex-direction:column;text-align:center;gap:12px;}
          .vp-amt-right{text-align:center;}
          .vp-card{padding:18px;}
        }
      `}</style>

            <div className="vp-page">
                {/* Header */}
                <div className="vp-hdr">
                    <div className="vp-hdr-left">
                        <button className="vp-back" onClick={() => navigate(-1)}>← Back</button>
                        <div>
                            <div className="vp-title">Payment Details</div>
                            <div className="vp-sub">Receipt #{p.payment_id || p.id}</div>
                        </div>
                    </div>
                    <div className="vp-actions">
                        <button className="vp-btn vp-btn-pdf" onClick={handleDownloadPDF}>
                            📄 Download PDF
                        </button>
                        <button className="vp-btn vp-btn-wa" onClick={handleShareWhatsApp}>
                            💬 Share WhatsApp
                        </button>
                        <button className="vp-btn vp-btn-edit" onClick={() => navigate("/dashboard/payments/edit", { state: { payment: p } })}>
                            ✏️ Edit
                        </button>
                    </div>
                </div>

                {/* Amount Card */}
                <div className="vp-amt-card">
                    <div>
                        <div className="vp-amt-label">Amount Received</div>
                        <div className="vp-amt-value">{fmt(p.amount)}</div>
                    </div>
                    <div className="vp-amt-right">
                        <span className={`vp-amt-status ${(p.status || "").toLowerCase() === "confirmed" ? "vp-amt-confirmed" : "vp-amt-pending"}`}>
                            {p.status || "—"}
                        </span>
                        <div style={{ fontSize: 12, opacity: .7, marginTop: 6 }}>{p.payment_mode || ""}</div>
                    </div>
                </div>

                {/* Detail Sections */}
                {sections.map((sec, si) => (
                    <div key={si} className="vp-card">
                        <div className="vp-sec-title">{sec.title}</div>
                        {sec.fields.map((f, fi) => (
                            <div key={fi} className="vp-row">
                                <span className="vp-label">{f.label}</span>
                                {f.badge ? (
                                    <span className={`vp-badge ${getStatusClass(f.value)}`}>{f.value || "—"}</span>
                                ) : f.highlight ? (
                                    <span className="vp-value vp-value-hl">{f.value || "—"}</span>
                                ) : (
                                    <span className="vp-value">{f.value || "—"}</span>
                                )}
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </>
    );
};

export default ViewPayment;
